import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { SUBMIT_REFERENCE } from 'graphql/resolvers/admin/candidates/mutations';
import {
  REFERENCE_SUBMISSION,
  GET_CANDIDATE_VERIFICATION_REQUEST
} from 'graphql/resolvers/client/queries';
import { referenceSubmitValidationSchema } from 'helpers/validators';
import { toastSuccess, toastError } from 'components/common/Toast';
import Loader from 'components/common/Loader';
import { svgs } from 'assets';
import ReferenceReport from 'components/VerificationReports/ReferenceReport';

// --- Warm, guided write-form palette & styles ---
// This is the form a referee (a stranger, often on a phone) fills out to
// vouch for a candidate. Distinct from the internal report: friendlier,
// reassuring, one clear question at a time.
const form = {
  ink: '#22303f',
  slate: '#6b7d8f',
  paper: '#faf7f0',
  cream: '#fffdf9',
  line: '#e7e0d0',
  teal: '#408996',
  tealDeep: '#2f6771',
  tealTint: 'rgba(64, 137, 150, 0.08)',
  amber: '#c8842d',
  blue: '#007bff',
  blueDeep: '#0062cc',
  blueTint: 'rgba(0, 123, 255, 0.08)'
};

const formStyles = {
  wrapper: {
    background: form.paper,
    minHeight: '100vh',
    padding: '32px 16px 90px'
  },
  shell: { maxWidth: '760px', margin: '0 auto' },
  formCard: {
    background: form.cream,
    borderRadius: '12px',
    border: `1px solid ${form.line}`,
    boxShadow: '0 1px 2px rgba(20, 30, 40, 0.04)',
    overflow: 'hidden'
  },
  headerCard: {
    background: form.blueTint,
    borderBottom: `1px solid rgba(0, 123, 255, 0.18)`,
    padding: '30px 30px 26px'
  },
  brandBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '22px'
  },
  brandLogo: { width: '52px', height: '52px', flexShrink: 0 },
  brandName: {
    fontSize: '1.3rem',
    fontWeight: '700',
    color: form.ink,
    letterSpacing: '0.2px'
  },
  intro: { textAlign: 'center' },
  eyebrow: {
    color: form.tealDeep,
    fontSize: '1.4rem',
    fontWeight: '700',
    letterSpacing: '0.5px',
    marginBottom: '12px'
  },
  subtitle: {
    color: form.ink,
    fontSize: '1.2rem',
    lineHeight: '1.55',
    maxWidth: '560px',
    margin: '0 auto'
  },
  progressTrack: {
    display: 'flex',
    gap: '5px',
    marginTop: '20px'
  },
  progressSegment: (filled) => ({
    flex: 1,
    height: '4px',
    borderRadius: '999px',
    background: filled ? form.teal : form.line
  }),
  progressLabel: {
    color: form.slate,
    fontSize: '1rem',
    fontWeight: '600',
    marginTop: '10px',
    letterSpacing: '0.3px'
  },
  card: {
    padding: '24px 0',
    borderBottom: `1px solid ${form.line}`
  },
  cardLast: {
    padding: '24px 0'
  },
  cardHead: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    marginBottom: '18px'
  },
  cardIndex: {
    flexShrink: 0,
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: form.tealTint,
    color: form.teal,
    fontWeight: '700',
    fontSize: '0.98rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  question: {
    fontSize: '1.15rem',
    fontWeight: '600',
    color: form.ink,
    lineHeight: '1.45'
  },
  helperText: {
    fontSize: '0.98rem',
    color: form.amber,
    fontWeight: '500',
    marginTop: '4px'
  },
  fieldLabel: {
    width: '90%',
    margin: '0 auto 8px',
    fontSize: '1rem',
    fontWeight: '600',
    color: form.slate,
    display: 'block'
  },
  required: { color: '#c0392b', marginLeft: '3px' },
  input: {
    width: '90%',
    margin: '0 auto',
    display: 'block',
    padding: '13px 14px',
    borderRadius: '8px',
    border: `1.5px solid ${form.line}`,
    fontSize: '1.16rem',
    color: form.ink,
    background: '#fff'
  },
  choiceGrid: (columns = 'auto-fit') => ({
    display: 'grid',
    gridTemplateColumns:
      columns === 'auto-fit' ? 'repeat(auto-fit, minmax(150px, 1fr))' : columns,
    gap: '10px',
    width: '90%',
    margin: '0 auto'
  }),
  choice: (selected) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '13px 16px',
    borderRadius: '8px',
    border: `1.5px solid ${selected ? form.blue : form.line}`,
    background: selected ? form.blue : '#fff',
    color: selected ? '#fff' : form.ink,
    fontWeight: selected ? '600' : '500',
    fontSize: '1.08rem',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background 0.15s ease'
  }),
  commentLabel: {
    width: '90%',
    fontSize: '1rem',
    fontWeight: '600',
    color: form.slate,
    margin: '16px auto 8px',
    display: 'block'
  },
  errorText: {
    width: '90%',
    margin: '8px auto 0',
    color: '#c0392b',
    fontSize: '1rem',
    fontWeight: '500'
  },
  agreementCard: {
    background: form.blueTint,
    border: `1px solid rgba(0, 123, 255, 0.25)`,
    borderRadius: '10px',
    padding: '20px 22px',
    marginBottom: '20px',
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start'
  },
  submitBar: { display: 'flex', justifyContent: 'flex-end' },
  submitButton: {
    padding: '15px 34px',
    borderRadius: '9px',
    border: 'none',
    background: form.blue,
    color: '#fff',
    fontWeight: '700',
    fontSize: '1.18rem',
    letterSpacing: '0.3px',
    cursor: 'pointer',
    boxShadow: '0 10px 22px -8px rgba(0, 98, 204, 0.45)'
  },

  // --- Read-only report-specific additions ---
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '999px',
    background: 'rgba(0, 123, 255, 0.12)',
    border: `1px solid rgba(0, 123, 255, 0.35)`,
    color: form.blueDeep,
    fontSize: '0.85rem',
    fontWeight: '700',
    letterSpacing: '0.3px'
  },
  summaryStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    borderBottom: `1px solid ${form.line}`
  },
  summaryCell: {
    padding: '22px 26px',
    borderRight: `1px solid ${form.line}`
  },
  summaryLabel: {
    fontSize: '0.95rem',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: form.slate,
    marginBottom: '6px'
  },
  summaryValue: {
    fontSize: '1.1rem',
    fontWeight: '700',
    color: form.ink,
    wordBreak: 'break-word'
  },
  rowLabel: {
    fontSize: '1rem',
    fontWeight: '600',
    letterSpacing: '0.3px',
    color: form.slate,
    marginBottom: '6px'
  },
  questionLabel: {
    fontSize: '1.15rem',
    fontWeight: '600',
    color: form.ink,
    marginBottom: '12px'
  },
  rowValue: {
    fontSize: '1.16rem',
    fontWeight: '600',
    color: form.ink,
    wordBreak: 'break-word'
  },
  answerCard: {
    border: `1px solid ${form.line}`,
    borderRadius: '10px',
    padding: '18px 20px',
    background: '#fbfaf7'
  },
  verdictPill: (active) => ({
    display: 'inline-block',
    padding: '9px 18px',
    borderRadius: '8px',
    fontSize: '1.08rem',
    fontWeight: '700',
    border: `1.5px solid ${active ? form.blue : form.line}`,
    background: active ? form.blue : '#fff',
    color: active ? '#fff' : form.slate
  }),
  commentBlock: {
    marginTop: '14px',
    paddingTop: '14px',
    borderTop: `1px dashed ${form.line}`,
    fontSize: '1.16rem',
    color: form.ink,
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap'
  },
  commentSubLabel: {
    fontSize: '1rem',
    fontWeight: '600',
    letterSpacing: '0.3px',
    textTransform: 'none',
    color: form.slate,
    marginBottom: '6px'
  },
  footerNote: {
    marginTop: '10px',
    paddingTop: '20px',
    borderTop: `1px solid ${form.line}`,
    fontSize: '1rem',
    color: '#000',
    textAlign: 'center'
  }
};

const PersonalReference = ({
  match: { params },
  employeeName: defaultEmployeeName = 'The Employee'
}) => {
  const requestId = params.id;
  const verificationId = params?.verificationId;
  const [showLoader, setLoaderState] = useState(false);
  const [createReferenceRequest] = useMutation(SUBMIT_REFERENCE);

  const { data: submissionData, refetch: refetchSubmission } = useQuery(
    REFERENCE_SUBMISSION,
    {
      variables: {
        referenceId: requestId,
        verificationId: verificationId
      },
      skip: !requestId
    }
  );

  const { data: candidateData } = useQuery(GET_CANDIDATE_VERIFICATION_REQUEST, {
    variables: { id: verificationId },
    skip: !verificationId
  });

  const candidate = candidateData?.verificationRequest?.candidate;
  const candidateName = candidate
    ? `${candidate.firstname || ''} ${candidate.lastname || ''}`.trim()
    : '';
  const employeeName = candidateName || defaultEmployeeName;

  const isReadOnly = !!submissionData?.referenceSubmission;
  const parsedExtraData = submissionData?.referenceSubmission?.formData
    ? JSON.parse(submissionData?.referenceSubmission?.formData)
    : {};

  const options = {
    relationship: ['Line Manager', 'Colleague', 'Subordinate', 'Friend'],
    duration: ['Under 1 year', '1-5 years', '5-10 years', 'Over 10 years'],
    honest: ['Yes', 'No', 'Not Certain'],
    recommend: ['Yes', 'No'],
    motivation: ['Great Extent', 'Low Extent', 'Not motivated & Driven']
  };

  const handleOnSubmit = async (values) => {
    setLoaderState(true);
    try {
      const response = await createReferenceRequest({
        variables: {
          input: {
            requestId: requestId,
            fullname: values.refereeName,
            email: values.email,
            phone: values.phone,
            address: values.address,
            formData: JSON.stringify({
              question_1_relationship: values.relationship,
              question_1_comments: values.relationshipComments,
              question_2_duration: values.durationKnown,
              question_2_comments: values.durationComments,
              question_3_honesty: values.isHonest,
              question_4_recommend: values.recommendation,
              question_4_reason: values.recommendationReason,
              question_5_motivation: values.motivationLevel,
              generalComments: values.generalComments,
              occupation: values.occupation
            })
          }
        }
      });

      if (response.data?.submitReferenceForm?.ok) {
        toastSuccess(svgs.SUCCESS_ICON, 'Reference Submitted Successfully');
        if (refetchSubmission) {
          await refetchSubmission();
        }
      } else {
        const errors = response.data?.submitReferenceForm?.errors?.messages.join(', ');
        toastError(svgs.DELETE_ICON, errors || 'Submission failed.');
      }
    } catch (e) {
      const errors = e?.data?.submitReferenceForm?.errors?.messages.join(', ');
      toastError(svgs.DELETE_ICON, errors || 'An error occurred during submission.');
    } finally {
      setLoaderState(false);
    }
  };

  const totalQuestions = 6;
  const answeredCount = (values) =>
    [
      values.refereeName,
      values.occupation,
      values.address,
      values.relationship,
      values.durationKnown,
      values.isHonest,
      values.recommendation,
      values.motivationLevel
    ].filter(Boolean).length;

  if (isReadOnly) {
    return (
      <ReferenceReport
        submission={submissionData?.referenceSubmission}
        extra={parsedExtraData}
        employeeName={employeeName}
      />
    );
  }

  return (
    <div style={formStyles.wrapper}>
      <Formik
        enableReinitialize
        initialValues={{
          relationship: parsedExtraData.question_1_relationship || '',
          relationshipComments: parsedExtraData.question_1_comments || '',
          durationKnown: parsedExtraData.question_2_duration || '',
          durationComments: parsedExtraData.question_2_comments || '',
          isHonest: parsedExtraData.question_3_honesty || '',
          recommendation: parsedExtraData.question_4_recommend || '',
          recommendationReason: parsedExtraData.question_4_reason || '',
          motivationLevel: parsedExtraData.question_5_motivation || '',
          generalComments: parsedExtraData.generalComments || '',
          refereeName: submissionData?.referenceSubmission?.fullname || '',
          address: submissionData?.referenceSubmission?.address || '',
          phone: submissionData?.referenceSubmission?.phone || '',
          email: submissionData?.referenceSubmission?.email || '',
          occupation: parsedExtraData.occupation || '',
          agreement: false
        }}
        validationSchema={referenceSubmitValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ values }) => (
          <div style={formStyles.shell}>
            <Form>
              <div style={formStyles.formCard}>
                <div style={formStyles.headerCard}>
                  <div style={formStyles.brandBar}>
                    <svgs.LOGO style={formStyles.brandLogo} />
                    <span style={formStyles.brandName}>Workforce Group</span>
                  </div>
                  <div style={formStyles.intro}>
                    <p style={formStyles.eyebrow}>Reference Form</p>
                    <p style={formStyles.subtitle}>
                      Please answer every question as honestly as possible. Your responses
                      are treated with the highest level of confidentiality and shared
                      only with the verifying employer.
                    </p>
                    <div style={formStyles.progressTrack}>
                      {Array.from({ length: totalQuestions }).map((_, i) => (
                        <div
                          key={i}
                          style={formStyles.progressSegment(i < answeredCount(values))}
                        />
                      ))}
                    </div>
                    <p style={formStyles.progressLabel}>
                      {answeredCount(values)} of {totalQuestions} questions answered
                    </p>
                  </div>
                </div>

                {/* 1. Referee identity */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>1</span>
                    <p style={formStyles.question}>
                      What is your full name and occupation?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label style={formStyles.fieldLabel}>Full Name</label>
                      <Field
                        name="refereeName"
                        type="text"
                        placeholder="e.g. Adaeze Nwosu"
                        style={formStyles.input}
                      />
                      <ErrorMessage
                        name="refereeName"
                        component="div"
                        style={formStyles.errorText}
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={formStyles.fieldLabel}>Occupation</label>
                      <Field
                        name="occupation"
                        type="text"
                        placeholder="e.g. HR Manager"
                        style={formStyles.input}
                      />
                      <ErrorMessage
                        name="occupation"
                        component="div"
                        style={formStyles.errorText}
                      />
                    </div>
                  </div>
                </div>

                {/* 2. Address */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>2</span>
                    <p style={formStyles.question}>
                      What is your address? <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <Field
                    name="address"
                    as="textarea"
                    rows="3"
                    placeholder="Full residential or business address"
                    style={formStyles.input}
                  />
                  <ErrorMessage
                    name="address"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* Contact Block */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>📞</span>
                    <p style={formStyles.question}>How can we reach you?</p>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label style={formStyles.fieldLabel}>
                        Telephone Number <span style={formStyles.required}>*</span>
                      </label>
                      <Field
                        name="phone"
                        type="tel"
                        placeholder="080..."
                        style={formStyles.input}
                      />
                      <ErrorMessage
                        name="phone"
                        component="div"
                        style={formStyles.errorText}
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={formStyles.fieldLabel}>
                        Email Address <span style={formStyles.required}>*</span>
                      </label>
                      <Field
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        style={formStyles.input}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        style={formStyles.errorText}
                      />
                    </div>
                  </div>
                </div>

                {/* 3. Relationship */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>3</span>
                    <p style={formStyles.question}>
                      What is your relationship with the employee?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div
                    style={formStyles.choiceGrid('repeat(auto-fit, minmax(150px, 1fr))')}
                  >
                    {options.relationship.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.relationship === opt)}
                      >
                        <Field
                          type="radio"
                          name="relationship"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="relationship"
                    component="div"
                    style={formStyles.errorText}
                  />
                  <label style={formStyles.commentLabel}>Comments (optional)</label>
                  <Field
                    name="relationshipComments"
                    as="textarea"
                    rows="2"
                    style={formStyles.input}
                  />
                </div>

                {/* 4. Duration */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>4</span>
                    <p style={formStyles.question}>
                      How long have you known the employee?
                    </p>
                  </div>
                  <div
                    style={formStyles.choiceGrid('repeat(auto-fit, minmax(150px, 1fr))')}
                  >
                    {options.duration.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.durationKnown === opt)}
                      >
                        <Field
                          type="radio"
                          name="durationKnown"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="durationKnown"
                    component="div"
                    style={formStyles.errorText}
                  />
                  <label style={formStyles.commentLabel}>Comments (optional)</label>
                  <Field
                    name="durationComments"
                    as="textarea"
                    rows="2"
                    style={formStyles.input}
                  />
                </div>

                {/* 5. Honesty */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>5</span>
                    <p style={formStyles.question}>
                      Do you consider them to be honest?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div style={formStyles.choiceGrid('repeat(3, 1fr)')}>
                    {options.honest.map((opt) => (
                      <label key={opt} style={formStyles.choice(values.isHonest === opt)}>
                        <Field
                          type="radio"
                          name="isHonest"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="isHonest"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* 6. Recommendation */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>6</span>
                    <p style={formStyles.question}>
                      Would you recommend them for employment with our client, and why?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div style={formStyles.choiceGrid('repeat(2, 1fr)')}>
                    {options.recommend.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.recommendation === opt)}
                      >
                        <Field
                          type="radio"
                          name="recommendation"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="recommendation"
                    component="div"
                    style={formStyles.errorText}
                  />
                  <label style={formStyles.commentLabel}>Reason</label>
                  <Field
                    name="recommendationReason"
                    as="textarea"
                    rows="2"
                    style={formStyles.input}
                  />
                  <ErrorMessage
                    name="recommendationReason"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* 7. Motivation */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>7</span>
                    <p style={formStyles.question}>
                      To what extent would you say they are self-motivated and driven to
                      achieve results?
                    </p>
                  </div>
                  <div
                    style={formStyles.choiceGrid('repeat(auto-fit, minmax(180px, 1fr))')}
                  >
                    {options.motivation.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.motivationLevel === opt)}
                      >
                        <Field
                          type="radio"
                          name="motivationLevel"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="motivationLevel"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* General Comments */}
                <div style={formStyles.cardLast}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>💬</span>
                    <p style={formStyles.question}>
                      Anything else you&apos;d like to add?
                    </p>
                  </div>
                  <Field
                    name="generalComments"
                    as="textarea"
                    rows="4"
                    placeholder="Provide any additional remarks here..."
                    style={formStyles.input}
                  />
                </div>
              </div>

              {/* Agreement & Submit */}
              <div style={formStyles.agreementCard}>
                <Field
                  type="checkbox"
                  name="agreement"
                  id="agreementCheck"
                  style={{
                    width: '20px',
                    height: '20px',
                    flexShrink: 0,
                    marginTop: '3px'
                  }}
                />
                <div>
                  <label
                    htmlFor="agreementCheck"
                    style={{
                      cursor: 'pointer',
                      lineHeight: '1.5',
                      color: form.ink,
                      fontWeight: 600,
                      fontSize: '1.05rem'
                    }}
                  >
                    By checking this box <span style={formStyles.required}>*</span>, I
                    certify that I am voluntarily standing as a referee for {employeeName}{' '}
                    and that the information provided above is true and correct to the
                    best of my knowledge.
                  </label>
                  <ErrorMessage
                    name="agreement"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>
              </div>

              <div style={formStyles.submitBar}>
                <button type="submit" style={formStyles.submitButton}>
                  {showLoader ? <Loader /> : 'Submit Reference'}
                </button>
              </div>
            </Form>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default PersonalReference;
